import {
  Checkbox,
  checkboxStates,
  Modal,
  NewButton,
  NewButtonVariant,
} from "@visiblethread/react-components";
import clsx from "clsx";
import { UserPermissionRow } from "components/admin";
import Field, { fieldTypes } from "components/Field";
import { CLOUD_ADMIN_LABEL, ON_PREM_ADMIN_LABEL } from "constants/systemAdmin";
import { modalTypes } from "constants/types";
import PropTypes from "prop-types";
import React, { useId, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { hideModal } from "slices/app/actions/modals";
import { selectIsCloudMode, selectIsGovMode } from "slices/app/selectors";
import {
  createUser,
  createUserWithWorkspace,
} from "slices/systemAdmin/actions/users";
import { selectIsUsernameAndPassword } from "slices/systemAdmin/selectors";
import { createUserAndAddToWorkspace } from "slices/userAdmin/actions/userAdmin";

import classes from "./NewUserModal.module.scss";

const NewUserModal = ({ workspaceId }) => {
  const dispatch = useDispatch();
  const formId = useId();

  const app = useSelector((state) => state.app);

  const isCloudMode = useSelector(selectIsCloudMode);
  const isGovMode = useSelector(selectIsGovMode);

  const [userName, setUserName] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [systemAdmin, setSystemAdmin] = useState(false);

  const [licensed, setLicensed] = useState(true);
  const [readabilityAdmin, setReadabilityAdmin] = useState(true);
  const [acronymAdmin, setAcronymAdmin] = useState(true);
  const [discoveryAdmin, setDiscoveryAdmin] = useState(true);
  const [excelAdmin, setExcelAdmin] = useState(true);
  const [dictionariesAdmin, setDictionariesAdmin] = useState(true);
  const [userAdmin, setUserAdmin] = useState(false);
  const [proposalStagesAdmin, setProposalStagesAdmin] = useState(true);
  const [docStyleTemplatesAdmin, setDocStyleTemplatesAdmin] = useState(true);
  const [editCurrenciesAdmin, setEditCurrenciesAdmin] = useState(true);
  const [archiveStatusesAdmin, setArchiveStatusesAdmin] = useState(true);

  const [userNameError, setUserNameError] = useState("");
  const [firstNameError, setFirstNameError] = useState(false);
  const [lastNameError, setLastNameError] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  const isUserNamePassword = useSelector(selectIsUsernameAndPassword);
  const showPasswordFields = !isGovMode && (isUserNamePassword || systemAdmin);

  const ssoButtonEnabled = () =>
    userName &&
    firstName &&
    lastName &&
    userNameError === "" &&
    firstNameError === false &&
    lastNameError === false;

  const userPassButtonEnabled = () =>
    userName &&
    firstName &&
    lastName &&
    password &&
    confirmPassword &&
    userNameError === "" &&
    firstNameError === false &&
    lastNameError === false &&
    passwordError === "" &&
    confirmPasswordError === "" &&
    password === confirmPassword;

  const isButtonEnabled = () => {
    if (isGovMode || !isUserNamePassword) {
      return ssoButtonEnabled();
    }
    return userPassButtonEnabled();
  };

  const isCloud = app?.session?.userInfo?.cloudMode;

  const isInSystemAdminPage =
    window.location.hash.includes("/system-admin") ||
    window.location.hash.includes("/cloud-admin");

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!isButtonEnabled()) {
      return;
    }

    if (isInSystemAdminPage) {
      if (isCloud) {
        dispatch(
          createUserWithWorkspace(
            userName?.trim(),
            firstName?.trim(),
            lastName?.trim(),
            password?.trim(),
            workspaceId,
            licensed,
            readabilityAdmin,
            acronymAdmin,
            discoveryAdmin,
            excelAdmin,
            proposalStagesAdmin,
            dictionariesAdmin,
            docStyleTemplatesAdmin,
            editCurrenciesAdmin,
            archiveStatusesAdmin,
            userAdmin
          )
        );
      } else {
        dispatch(
          createUser(
            userName?.trim(),
            firstName?.trim(),
            lastName?.trim(),
            password?.trim(),
            systemAdmin
          )
        );
      }
    } else {
      dispatch(
        createUserAndAddToWorkspace(
          userName?.trim(),
          firstName?.trim(),
          lastName?.trim(),
          password?.trim(),
          licensed,
          readabilityAdmin,
          acronymAdmin,
          discoveryAdmin,
          excelAdmin,
          proposalStagesAdmin,
          dictionariesAdmin,
          docStyleTemplatesAdmin,
          editCurrenciesAdmin,
          archiveStatusesAdmin,
          userAdmin
        )
      );
    }
  };

  const showPermissionsOptions = () => isCloud || !isInSystemAdminPage;

  const handleCancel = () => {
    dispatch(hideModal(modalTypes.NEW_USER));
  };

  const handleLicensedChange = () => {
    if (licensed) {
      setDictionariesAdmin(false);
      setReadabilityAdmin(false);
      setAcronymAdmin(false);
      setDiscoveryAdmin(false);
      setExcelAdmin(false);
      setProposalStagesAdmin(false);
      setDocStyleTemplatesAdmin(false);
      setEditCurrenciesAdmin(false);
      setArchiveStatusesAdmin(false);
    }
    setLicensed(!licensed);
  };

  const adminCheckbox = () => (
    <div className="new__user__modal__admin">
      <span>
        <b>System Administrator</b>
      </span>
      <Checkbox
        state={systemAdmin ? checkboxStates.checked : checkboxStates.empty}
        handleChange={() => {
          setSystemAdmin(!systemAdmin);
        }}
        disabled={false}
        id="cb-new-user-system-admin"
        value="new-user-system-admin-checkbox"
      />
    </div>
  );

  return (
    <Modal
      data-testid="newUserModal"
      className={clsx("fade-in", classes.modal_container)}
      show
      onDismiss={handleCancel}
      backdrop
      centered
    >
      <Modal.Header>
        <b className="new__user__modal__header">Add User</b>
      </Modal.Header>
      <Modal.Body className={classes.modal_body}>
        <form id={formId} className="new__user__modal" onSubmit={handleSubmit}>
          <Field
            type={fieldTypes.userName}
            propsObject={{
              userName,
              setUserName,
              userNameError,
              setUserNameError,
            }}
          />

          <Field
            type={fieldTypes.firstName}
            propsObject={{
              firstName,
              setFirstName,
              firstNameError,
              setFirstNameError,
            }}
          />

          <Field
            type={fieldTypes.lastName}
            propsObject={{
              lastName,
              setLastName,
              lastNameError,
              setLastNameError,
            }}
          />

          {showPasswordFields && (
            <Field
              type={fieldTypes.passwordAndConfirm}
              propsObject={{
                password,
                setPassword,
                passwordError,
                setPasswordError,
                confirmPassword,
                setConfirmPassword,
                confirmPasswordError,
                setConfirmPasswordError,
              }}
            />
          )}
          {showPermissionsOptions() && (
            <div className="edit__permissions__modal">
              <UserPermissionRow
                heading
                checked={licensed}
                disabled={false}
                id="edit-permissions-licensed"
                onChange={handleLicensedChange}
                text="Is Licensed User"
                tooltipText="Making a user 'active' allows them sign in and work with documents. Only 'Active' users use a license."
              />
              <UserPermissionRow
                checked={dictionariesAdmin}
                disabled={!licensed}
                id="edit-permissions-dictionaries"
                onChange={() => setDictionariesAdmin(!dictionariesAdmin)}
                text="Can Edit Dictionaries"
                tooltipText="Enabling this permission allows this user create and modify dictionaries."
              />
              <UserPermissionRow
                checked={readabilityAdmin}
                disabled={!licensed}
                id="edit-permissions-readability-settings"
                onChange={() => setReadabilityAdmin(!readabilityAdmin)}
                text="Can Edit Readability Settings"
                tooltipText="Enabling this permission allows this user edit the Readability Settings for this workspace."
              />
              <UserPermissionRow
                checked={acronymAdmin}
                disabled={!licensed}
                id="edit-permissions-acronym-settings"
                onChange={() => setAcronymAdmin(!acronymAdmin)}
                text="Can Edit Acronym Settings"
                tooltipText="Enabling this permission allows this user edit the Acronym Settings for this workspace."
              />
              <UserPermissionRow
                checked={discoveryAdmin}
                disabled={!licensed}
                id="edit-permissions-discovery-settings"
                onChange={() => setDiscoveryAdmin(!discoveryAdmin)}
                text="Can Edit Discovery Settings"
                tooltipText="Enabling this permission allows this user edit the Discovery Settings for this workspace."
              />
              <UserPermissionRow
                checked={excelAdmin}
                disabled={!licensed}
                id="edit-permissions-excel__outputs"
                onChange={() => setExcelAdmin(!excelAdmin)}
                text="Can Edit Excel Outputs"
                tooltipText="Enabling this permission allows this user edit the Excel Outputs for this workspace."
              />
              <UserPermissionRow
                checked={proposalStagesAdmin}
                disabled={!licensed}
                id="edit-permissions-proposal-stages"
                onChange={() => setProposalStagesAdmin(!proposalStagesAdmin)}
                text="Can Edit Pipeline Stages"
                tooltipText="Enabling this permission allows this user edit the Pipeline Stages for this workspace."
              />
              <UserPermissionRow
                checked={docStyleTemplatesAdmin}
                disabled={!licensed}
                id="edit-permissions-templates"
                onChange={() =>
                  setDocStyleTemplatesAdmin(!docStyleTemplatesAdmin)
                }
                text="Can Edit Style Templates"
                tooltipText="Enabling this permission allows this user edit the Templates for this workspace."
              />
              <UserPermissionRow
                checked={editCurrenciesAdmin}
                disabled={!licensed}
                id="edit-permissions-currencies"
                onChange={() => setEditCurrenciesAdmin(!editCurrenciesAdmin)}
                text="Can Edit Currencies"
                tooltipText="Enabling this permission allows this user edit the Currencies for this workspace."
              />
              <UserPermissionRow
                checked={archiveStatusesAdmin}
                disabled={!licensed}
                id="edit-permissions-archive-statuses"
                onChange={() =>
                  setArchiveStatusesAdmin(!archiveStatusesAdmin)
                }
                text="Edit Archive Statuses"
                tooltipText="Enabling this permission allows this user edit the Archive Statuses for this workspace."
              />
              <UserPermissionRow
                heading
                checked={userAdmin}
                disabled={false}
                id="edit-permissions-user-admin"
                onChange={() => setUserAdmin(!userAdmin)}
                text={isCloudMode ? CLOUD_ADMIN_LABEL : ON_PREM_ADMIN_LABEL}
                tooltipText="This allows the user create modify and delete users within the workspace."
              />
            </div>
          )}
          {isInSystemAdminPage && !isCloud && adminCheckbox()}
        </form>
      </Modal.Body>
      <Modal.Footer>
        <NewButton
          data-testid="cancelButton"
          variant={NewButtonVariant.l2}
          onClick={handleCancel}
        >
          Cancel
        </NewButton>

        <NewButton
          type="submit"
          form={formId}
          data-testid="saveButton"
          variant={NewButtonVariant.l1}
          disabled={!isButtonEnabled()}
        >
          Add User
        </NewButton>
      </Modal.Footer>
    </Modal>
  );
};

export default NewUserModal;

NewUserModal.propTypes = {
  workspaceId: PropTypes.number,
};
